import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, useAuth } from "@/context/AuthContext";

export default function TheoryHome() {
  const navigate = useNavigate();
  const { authReady } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [category, setCategory] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(() => {
    setError("");
    setLoading(true);
    return Promise.all([
      api.get("/theory/overview"),
      api.get("/theory/history"),
    ])
      .then(([overviewRes, historyRes]) => {
        setData(overviewRes.data);
        setHistory(historyRes.data.sessions || []);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          setError("Your session expired. Please log in again.");
        } else {
          setError("Could not load Theory Test Preparation. Please try again.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!authReady) return;
    loadOverview();
  }, [authReady, loadOverview]);

  const start = async (mode, opts = {}) => {
    setError("");
    setBusy(mode === "practice" && opts.with_visuals ? "practice-visuals" : mode);
    try {
      const url = mode === "mock" ? "/theory/mock/start" : "/theory/practice/start";
      const payload =
        mode === "mock"
          ? {}
          : { category: category || null, with_visuals: Boolean(opts.with_visuals) };
      const { data: res } = await api.post(url, payload);
      if (mode === "practice" && (res.status === "interrupted" || res.session_interrupted || !res.question)) {
        setError(
          res.status === "interrupted" || res.session_interrupted
            ? "An older interrupted session blocked practice start. Use Start new practice on that session or try again."
            : "Practice could not load a question. Please try again."
        );
        return;
      }
      navigate(`/theory/session/${res.session_id}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  if (!authReady || loading) return <p data-testid="theory-loading">Loading your quiz…</p>;
  if (error && !data) {
    return (
      <div className="space-y-3" data-testid="theory-error-panel">
        <p role="alert" className="text-destructive" data-testid="theory-error">{error}</p>
        <button type="button" className="btn-outline" onClick={loadOverview} data-testid="theory-retry-button">
          Retry
        </button>
      </div>
    );
  }
  if (!data) return <p data-testid="theory-loading">Loading your quiz…</p>;

  const pub = data.theory_publication || {};
  const maintenance = Boolean(pub.maintenance);
  const maintenanceMessage =
    pub.maintenance_message ||
    "Theory practice and mock tests are temporarily unavailable while question publication is being verified.";

  return (
    <div className="space-y-8" data-testid="theory-home">
      <header className="space-y-1">
        <h1 className="text-4xl sm:text-5xl font-bold text-primary" data-testid="theory-heading">
          Theory Test Preparation
        </h1>
        <p className="text-base md:text-lg text-muted-foreground">
          New Zealand learner licence theory practice — {data.practice_eligible} verified published questions.
        </p>
      </header>

      {maintenance && (
        <section className="card space-y-2 border-amber-200 bg-amber-50" role="alert" data-testid="theory-maintenance-banner">
          <h2 className="text-base font-semibold">Theory practice temporarily unavailable</h2>
          <p className="text-sm text-muted-foreground" data-testid="theory-maintenance-message">{maintenanceMessage}</p>
          <p className="text-sm text-muted-foreground">
            Your completed sessions and progress are preserved. Other Academy features are unaffected.
          </p>
        </section>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <section className="card space-y-4" data-testid="practice-card">
          <h2 className="text-base md:text-lg font-semibold">Practice Mode</h2>
          <p className="text-muted-foreground">
            One question at a time with immediate feedback, explanations and official source links.
          </p>
          <div className="space-y-1">
            <label htmlFor="practice-category" className="font-medium text-sm">Topic (optional)</label>
            <select
              id="practice-category"
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="practice-category-select"
            >
              <option value="">All topics</option>
              {data.categories.map((c) => (
                <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={maintenance || busy === "practice"}
            onClick={() => start("practice")}
            data-testid="practice-start-button"
          >
            {busy === "practice" ? "Starting…" : "Start practice"}
          </button>
          {(data.visual_overlay_count || 0) > 0 && (
            <button
              type="button"
              className="btn-outline"
              disabled={maintenance || busy === "practice-visuals"}
              onClick={() => start("practice", { with_visuals: true })}
              data-testid="practice-visuals-start-button"
            >
              {busy === "practice-visuals" ? "Starting…" : "Practice with diagrams"}
            </button>
          )}
        </section>

        <section className="card space-y-4" data-testid="mock-card">
          <h2 className="text-base md:text-lg font-semibold">Practice Mock Test</h2>
          <p className="text-muted-foreground">
            {data.mock_question_count} questions, no feedback until the end. Pass mark: {data.mock_pass_mark}/{data.mock_question_count}.
            This is a practice mock test — it is not an official NZTA test result.
          </p>
          <button
            type="button"
            className="btn-accent"
            disabled={maintenance || !pub.mock_available || busy === "mock"}
            onClick={() => start("mock")}
            data-testid="mock-start-button"
          >
            {busy === "mock" ? "Preparing…" : "Start mock test"}
          </button>
        </section>
      </div>

      {data.active_sessions.length > 0 && (
        <section className="card space-y-3" data-testid="active-sessions">
          <h2 className="text-base md:text-lg font-semibold">Resume an active session</h2>
          <ul className="space-y-2">
            {data.active_sessions.map((s) => (
              <li key={s.session_id} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {s.mode === "mock" ? "Practice Mock Test" : `Practice${s.category ? ` — ${s.category}` : ""}`}
                  {s.status === "interrupted" ? " — interrupted (publication review)" : ""}
                  {" "}({s.answered_count}/{s.total} answered)
                </span>
                <Link to={`/theory/session/${s.session_id}`} className="btn-outline" data-testid={`resume-session-${s.session_id}`}>
                  Resume
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {history.length > 0 && (
        <section className="card space-y-3" data-testid="theory-history">
          <h2 className="text-base md:text-lg font-semibold">Attempt history</h2>
          <ul className="space-y-2">
            {history.map((s) => (
              <li key={s.session_id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`history-${s.session_id}`}>
                <span className="text-sm">
                  {new Date(s.created_at).toLocaleString()} — {s.mode === "mock" ? "Practice Mock Test" : "Practice"}
                  {s.category ? ` (${s.category})` : ""} — {s.status === "completed" && s.result
                    ? s.mode === "mock"
                      ? `${s.result.score_correct}/${s.result.total} — ${s.result.passed ? "Pass" : "Not yet passed"}`
                      : `${s.result.score_correct}/${s.result.total} correct`
                    : s.status === "interrupted"
                      ? "Interrupted — publication review"
                      : "In progress"}
                </span>
                {s.status === "completed" && (
                  <Link to={`/theory/session/${s.session_id}`} className="underline" data-testid={`review-session-${s.session_id}`}>
                    Review
                  </Link>
                )}
                {s.status === "interrupted" && (
                  <Link to={`/theory/session/${s.session_id}`} className="underline" data-testid={`view-interrupted-${s.session_id}`}>
                    View interrupted session
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-sm text-muted-foreground" data-testid="theory-bank-note">
        Questions: {data.total_bank} approved learner-theory questions.
        {data.image_gap_excluded > 0 && ` ${data.image_gap_excluded} image-dependent question(s) are excluded until approved visuals are available.`}
      </p>
    </div>
  );
}
